import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Cpu, MemoryStick, Thermometer, HardDrive, Box, Database, Copy, Archive } from 'lucide-react';
import { adminApi as api } from '@/lib/api';
import { useLiveChannel } from '@/lib/live';
import DashboardShell from '@/components/enterprise/DashboardShell';
import { Panel, Kpi, StatusPill, Bar, ConnectionState } from '@/components/enterprise/ui';
import { Gauge } from '@/components/enterprise/charts';

export default function ProxmoxDashboard() {
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/dashboards/proxmox').then((r) => setSnap(r.data)).catch(() => setSnap({ status: 'offline', source: 'proxmox' })).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const onTick = useCallback((msg) => {
    if (!msg || !msg.data) return;
    setSnap((prev) => (prev && prev.data ? { ...prev, data: { ...prev.data, nodes: msg.data.nodes || prev.data.nodes, vms: msg.data.vms || prev.data.vms }, ts: msg.ts } : prev));
  }, []);
  useLiveChannel('proxmox', onTick);

  const ok = snap && snap.status === 'connected' && snap.data;
  const d = ok ? snap.data : null;

  return (
    <DashboardShell title="Cloud / Proxmox Operations Center" subtitle={d ? `cluster ${d.cluster.name} · PVE ${d.cluster.version} · source: ${snap.source}` : 'proxmox-ve'}>
      {loading && <div className="text-sm text-[color:var(--itw-text-3)] font-mono">loading cluster…</div>}
      {!loading && !ok && <Panel testid="proxmox-offline"><ConnectionState status={snap ? snap.status : 'offline'} source={snap ? snap.source : 'proxmox'} onRetry={load} /></Panel>}
      {ok && (
        <div className="space-y-6" data-testid="proxmox-dashboard">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi label="Nodes online" value={`${d.cluster.nodes_online}/${d.cluster.nodes_total}`} sub={d.cluster.ha_state} accent="#2EF2A5" testid="proxmox-kpi-nodes" />
            <Kpi label="VMs running" value={`${d.cluster.vms_running}/${d.cluster.vms_total}`} sub="qemu" testid="proxmox-kpi-vms" />
            <Kpi label="LXC" value={d.cluster.lxc_total} sub="container" />
            <Kpi label="Quorum" value={d.cluster.quorum ? 'OK' : 'LOST'} sub="cluster" accent={d.cluster.quorum ? '#2EF2A5' : '#FF5C7A'} />
            <Kpi label="HA State" value={d.cluster.ha_state} accent="#2EF2A5" />
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            {d.nodes.map((n) => (
              <Panel key={n.id} title={n.id} subtitle={`${n.cores} cores · up ${n.uptime_days}d`} status={n.status} testid={`proxmox-node-${n.id}`}>
                <div className="flex items-center gap-4">
                  <Gauge value={n.cpu} label="CPU" size={92} />
                  <div className="flex-1 space-y-3">
                    <div>
                      <div className="flex items-center justify-between text-[11px] text-[color:var(--itw-text-2)] mb-1"><span className="flex items-center gap-1"><MemoryStick className="h-3 w-3" /> RAM</span><span className="font-mono">{n.mem_used}/{n.mem_total} GB</span></div>
                      <Bar value={n.mem_used} max={n.mem_total} />
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-[color:var(--itw-text-2)]">
                      <span className="flex items-center gap-1"><Thermometer className="h-3 w-3" /> {n.temp}°C</span>
                      <span className="font-mono">net {n.net_in}/{n.net_out} MB/s</span>
                    </div>
                  </div>
                </div>
              </Panel>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-4">
            <Panel title="Virtual Machines & Container" subtitle="qemu / lxc inventory" className="lg:col-span-2" testid="proxmox-vms">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono text-left">
                    <th className="py-2 font-normal">VMID</th><th className="py-2 font-normal">Name</th><th className="py-2 font-normal">Node</th><th className="py-2 font-normal">CPU</th><th className="py-2 font-normal">MEM</th><th className="py-2 font-normal">Status</th>
                  </tr></thead>
                  <tbody className="font-mono text-[13px]">
                    {d.vms.map((v) => (
                      <tr key={v.vmid} className="border-t border-[color:var(--itw-border)]">
                        <td className="py-2 text-[color:var(--itw-text-3)]">{v.vmid}</td>
                        <td className="py-2 text-[color:var(--itw-text)]">{v.name} <span className="text-[10px] text-[color:var(--itw-text-3)]">{v.type}</span></td>
                        <td className="py-2 text-[color:var(--itw-text-2)]">{v.node}</td>
                        <td className="py-2 w-24"><Bar value={v.cpu} max={100} height={5} /></td>
                        <td className="py-2 w-24"><Bar value={v.mem} max={100} height={5} color="#5AF3FF" /></td>
                        <td className="py-2"><StatusPill status={v.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
            <Panel title="Storage Pools" subtitle="capacity" testid="proxmox-storage">
              <div className="space-y-4">
                {d.storage.map((s) => (
                  <div key={s.id}>
                    <div className="flex items-center justify-between text-[12px] mb-1"><span className="flex items-center gap-1.5 text-[color:var(--itw-text)]"><Database className="h-3.5 w-3.5 text-[color:var(--itw-cyan)]" />{s.id}</span><span className="font-mono text-[color:var(--itw-text-3)]">{s.used_pct}%</span></div>
                    <Bar value={s.used_pct} max={100} color={s.used_pct > 70 ? '#FFCC66' : 'var(--itw-cyan)'} />
                    <div className="mt-1 text-[10px] font-mono text-[color:var(--itw-text-3)]">{s.used_gb} / {s.total_gb} GB · {s.type}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Panel title="Backups" subtitle="vzdump" testid="proxmox-backups">
              <div className="space-y-2">
                {d.backups.map((b) => (
                  <div key={b.id} className="flex items-center justify-between rounded-lg border border-[color:var(--itw-border)] px-3 py-2 text-[13px] font-mono">
                    <span className="flex items-center gap-2 text-[color:var(--itw-text)]"><Archive className="h-3.5 w-3.5 text-[color:var(--itw-text-3)]" />{b.vm}</span>
                    <span className="flex items-center gap-3"><span className="text-[color:var(--itw-text-3)]">{b.size_gb} GB · {b.when}</span><StatusPill status={b.status === 'ok' ? 'ok' : 'running'} /></span>
                  </div>
                ))}
              </div>
            </Panel>
            <Panel title="Replication" subtitle="zfs async" testid="proxmox-replication">
              <div className="space-y-2">
                {d.replication.map((rp) => (
                  <div key={rp.id} className="flex items-center justify-between rounded-lg border border-[color:var(--itw-border)] px-3 py-2 text-[13px] font-mono">
                    <span className="flex items-center gap-2 text-[color:var(--itw-text)]"><Copy className="h-3.5 w-3.5 text-[color:var(--itw-text-3)]" />{rp.source} → {rp.target}</span>
                    <span className="flex items-center gap-3"><span className="text-[color:var(--itw-text-3)]">{rp.last}</span><StatusPill status={rp.status} /></span>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </div>
      )}
    </DashboardShell>
  );
}
